import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import { getSubmissionsByAssignmentId, proposeQuestionSplit } from '../../../service';
import { saveReviewedAnswer } from '../utils/saveReviewedAnswer';
import { feedbackStatusOf, isBatchResubmission, isBulkResponseEditable } from '../utils/feedbackRun';
import { BulkUploadQuestionSplitReview } from './BulkUploadQuestionSplitReview';
import {
  isSplitResolved,
  splitStateFromProposal,
} from '../utils/questionSplit';
import eyeIcon from '../../../static/icons/Eye.svg';
import helpIcon from '../../../static/img/helpgray24.svg';
import arrowLeft from '../../../static/img/darkgray16leftarrow.svg';
import arrowRight from '../../../static/img/darkgray16rightarrow.svg';
import arrowDown from '../../../static/img/arrowdown2.svg';
import arrowUp from '../../../static/img/arrowup2.svg';
import zoomIcon from '../../../static/icons/black-magnifying-icon.svg';
import { Avatar } from '@boringer-avatars/react';
import QuestionTooltip from '../../../components2/QuestionTooltip';
import { BulkUploadWizardShell } from './BulkUploadWizardShell';
import QuestionRichTextEditor from '../../CreateNewTask/components/QuestionRichTextEditor';
import { BulkUploadPageGallery } from './BulkUploadPageGallery';
import { htmlToPlainText } from '../utils/html';
import GeneralPopup from '../../GeneralPopup';
import TopBannerBox from '../../../components2/TopBannerBox';
import warningIcon from '../../../static/icons/Warning-icon.svg';
import {
  BulkUploadReviewContent,
  BulkUploadReviewEmptyState,
  BulkUploadReviewLayout,
  BulkUploadReviewMain,
  BulkUploadReviewNav,
  BulkUploadReviewNavButton,
  BulkUploadReviewNavCenter,
  BulkUploadReviewPageDot,
  BulkUploadReviewPagination,
  BulkUploadReviewStudentMeta,
  BulkUploadReviewStudentName,
  BulkUploadReviewViewOriginal,
  BulkUploadReviewViewer,
  BulkUploadReviewViewerHeader,
  BulkUploadSectionTitle,
  BulkUploadAddStudentLink,
  BulkUploadStudentAvatar,
  BulkUploadStudentCard,
  BulkUploadStudentCardActions,
  BulkUploadStudentCardIcon,
  BulkUploadMoreDots,
  BulkUploadStudentCardRow,
  BulkUploadStudentInfo,
  BulkUploadStudentList,
  BulkUploadStudentListHeader,
  BulkUploadStudentListPanel,
  BulkUploadStudentName,
  BulkUploadStudentPages,
  BulkUploadStudentThumb,
  BulkUploadStudentThumbBody,
  BulkUploadStudentThumbHoverOverlay,
  BulkUploadStudentThumbNumber,
  BulkUploadStudentThumbs,
} from '../styles';

const STUDENT_LIST_HELP_TEXT =
  'Names in student list can be anything, not just student names';

const ReviewPageThumb = ({ page, onOpenGallery, currentResponseText }) => {
  if (!page?.id) {
    return null;
  }

  // For manual pages, show the current edited text instead of original content
  const displayText = page.source === 'manual' && currentResponseText
    ? currentResponseText
    : page.content;


  return (
    <BulkUploadStudentThumb
      $review
      role="button"
      tabIndex={0}
      aria-label={`Preview page ${page.pageNumber}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpenGallery(page.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpenGallery(page.id);
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <BulkUploadStudentThumbBody $review>
        {page.source === 'manual' && displayText ? (
          <span
            style={{
              display: 'block',
              padding: 4,
              fontSize: 7,
              lineHeight: 1.2,
              color: '#4b464f',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            {htmlToPlainText(displayText)}
          </span>
        ) : (
          <img src={page.thumbnail || page.fileUrl} alt="" draggable={false} />
        )}
        <BulkUploadStudentThumbHoverOverlay aria-hidden="true">
          <img src={zoomIcon} alt="" width={12} height={12} />
        </BulkUploadStudentThumbHoverOverlay>
        <BulkUploadStudentThumbNumber>{page.pageNumber}</BulkUploadStudentThumbNumber>
      </BulkUploadStudentThumbBody>
    </BulkUploadStudentThumb>
  );
};


const formatPagesLabel = (pages = []) => {
  if (!pages.length) return null;
  return (
    <>
      Pages: <strong>{pages.length}</strong>
    </>
  );
};

const EditorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;

  .question-quill-shell {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: none !important;
    border-radius: 0 !important;
  }

  .ql-toolbar.ql-snow,
  .ql-container.ql-snow {
    border-radius: 0 !important;
  }

  .ql-container {
    flex: 1;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  .ql-editor {
    overflow-y: auto !important;
    max-height: none !important;
  }

  .ql-container .ql-editor.ql-blank::before {
    text-align: left !important;
    left: 12px !important;
    right: auto !important;
  }

  .ql-toolbar {
    justify-content: center !important;
    flex-shrink: 0;
  }

  .ql-toolbar button,
  .ql-toolbar .ql-picker {
    margin-right: 8px !important;
  }
`;

/**
 * What extraction is doing for one student, read off their own pages.
 *
 * The pages are the one place this lives: mapSubmissionsOntoPages stamps the
 * submission's status and reason onto every page routed to that student, and the
 * flow keeps doing it on every poll tick. Reading a second copy off the row would
 * be a second source of truth for the same three fields.
 *
 * Manual pages are excluded because nothing is ever extracted for them — a
 * student who typed their whole response is not "still converting", and gating
 * their editor on a status that will never arrive would lock it forever.
 */
const FAILED_STATUSES = new Set(['FAILED', 'TIMED_OUT']);
const CONVERTING_STATUSES = new Set(['PENDING', 'EXTRACTING']);

const EXTRACTION_UNAVAILABLE = "We couldn't read this response.";

const extractedPagesOf = (student) =>
  (student?.originalPages || []).filter((page) => page?.source !== 'manual');

const failedPageOf = (student) =>
  extractedPagesOf(student).find((page) =>
    FAILED_STATUSES.has(page?.extractionStatus),
  ) || null;

/**
 * Unknown counts as converting. Before the first poll lands a page has no status
 * at all, and the safe reading of "we do not know yet" is the one that keeps the
 * editor locked — text typed into a row that is still converting is overwritten
 * the moment its extraction arrives.
 */
const isStudentConverting = (student) => {
  const pages = extractedPagesOf(student);
  if (pages.length === 0) return false;
  return pages.some(
    (page) =>
      !page?.extractionStatus || CONVERTING_STATUSES.has(page.extractionStatus),
  );
};

const ExtractionChip = ({ student }) => {
  const failed = failedPageOf(student);
  if (failed) {
    return (
      <StatusChip $failed data-testid="review-extraction-failure">
        {failed.failureReason || EXTRACTION_UNAVAILABLE}
      </StatusChip>
    );
  }
  if (!isStudentConverting(student)) return null;
  return <StatusChip data-extraction-chip="converting">Converting…</StatusChip>;
};

const editorPlaceholder = ({ isConverting, isManualText }) => {
  if (isConverting) return 'Still converting this response…';
  return isManualText
    ? 'Type your response here...'
    : 'Extracted text will appear here...';
};

/**
 * Read-only while this student is still converting.
 *
 * Extraction lands on the answer whole, so anything typed into a row that has not
 * settled is overwritten the moment its text arrives — silently, and it is the
 * teacher's own work. QuestionRichTextEditor already calls quill.enable(!disabled),
 * so the lock is the prop.
 */
const StatusChip = styled.span`
  align-self: flex-start;
  margin-top: 2px;
  padding: 1px 8px;
  border-radius: 999px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  color: ${({ $failed }) => ($failed ? '#b3261e' : '#7200e0')};
  background: ${({ $failed }) => ($failed ? '#fceceb' : '#f3e8ff')};
`;

const RichTextEditorWrapper = ({
  text,
  onChange,
  studentId,
  isManualText,
  isConverting = false,
  isReadOnly = false,
}) => {
  return (
    <EditorContainer>
      <QuestionRichTextEditor
        value={text || ''}
        plainText={!/<\/?(?:p|div|br|ol|ul|li|h[1-6]|blockquote|pre|strong|em|b|i|u|span|a)(?:\s|\/?>)/i.test(text || '')}
        onChange={onChange}
        placeholder={editorPlaceholder({ isConverting, isManualText })}
        disabled={isConverting || isReadOnly}
        hasFooter={false}
        toolbarVariant="response"
      />
    </EditorContainer>
  );
};

/**
 * Bulk Upload v2 (06 §7.3): the pages to show for a student — the committed
 * pages' server previews when the flow has them (`pageImagesForStudent`),
 * else the v1 file previews (no prop, or the images were purged).
 */
const reviewPagesOf = (student, pageImagesForStudent) => {
  const images = student?.id && pageImagesForStudent ? pageImagesForStudent(student.id) || [] : [];
  if (!images.length) return student?.originalPages || [];
  return images.map((image, index) => ({
    id: image.pageId,
    source: 'file',
    thumbnail: image.previewUrl || image.thumbUrl,
    thumbUrl: image.thumbUrl || image.previewUrl,
    pageNumber: index + 1,
  }));
};

const buildStudents = (rows = []) =>
  rows.map((row) => ({
    id: row.id,
    name: row.name || row.studentName,
    pages: row.pages ?? [],
    originalPages: row.originalPages ?? [],
    responseText: row.responseText ?? '',
  }));

const BulkUploadTeacherReview = ({
  students,
  assignmentId,
  submissionIds,
  isManualReview = false,
  isSubmitting = false,
  protectedStudentIds = new Set(),
  onEditResponse,
  // Multi-question tasks: the splitter replaces the editor, and the split state
  // lives in the flow (keyed by studentId) so it survives stepping back and forth.
  multiQuestion = false,
  questionSplits,
  onQuestionSplitChange,
  pageImagesForStudent,
}) => {
  // Filter students with assigned pages
  const studentsWithPages = useMemo(
    () => students.filter((s) => (s.pages?.length || 0) > 0),
    [students]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [galleryState, setGalleryState] = useState(null);
  const [responseTexts, setResponseTexts] = useState(() =>
    students.reduce((acc, student) => {
      acc[student.id] = student.responseText || '';
      return acc;
    }, {}),
  );
  const [expandedStudentId, setExpandedStudentId] = useState(() => studentsWithPages[0]?.id || null);
  const [submissions, setSubmissions] = useState([]);
  const protectedStudentIdsRef = useRef(protectedStudentIds);
  protectedStudentIdsRef.current = protectedStudentIds;

  // Track the last known responseText from props to detect changes
  const lastPropsResponseTextRef = useRef({});

  // Fetch submissions for auto-save
  useEffect(() => {
    if (!assignmentId) return;
    if (isManualReview && isSubmitting) {
      // A partial submit changes which responses may still be corrected. Keep
      // them locked until the post-attempt read identifies the remaining drafts.
      setSubmissions([]);
      return;
    }

    let cancelled = false;

    getSubmissionsByAssignmentId(assignmentId)
      .then((subs) => {
        if (cancelled) return;
        const wanted = submissionIds?.length ? new Set(submissionIds) : null;
        setSubmissions(wanted ? subs.filter(row => wanted.has(row.id)) : subs);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [assignmentId, submissionIds, isManualReview, isSubmitting]);

  /**
   * Multi-question tasks: one proposal per submission, asked for when its student
   * is first shown and its text has finished converting — a proposal read off a
   * half-extracted paper would place every question in the wrong place.
   */
  const [splitLoads, setSplitLoads] = useState({});
  const splitRequestRef = useRef(0);
  const activeStudentForSplit = studentsWithPages[activeIndex];
  const activeSubmissionForSplit = submissions.find(
    (row) => String(row.studentId) === String(activeStudentForSplit?.id),
  );
  const activeSplitSubmissionId = activeSubmissionForSplit?.id;
  const activeSplitStudentId = activeStudentForSplit?.id;
  const hasActiveSplit = Boolean(questionSplits?.[String(activeSplitStudentId)]);
  const activeIsConverting = isStudentConverting(activeStudentForSplit);
  const activeIsProtected = protectedStudentIds.has(String(activeSplitStudentId));
  const loadSplit = useCallback((submissionId, studentId) => {
    const request = ++splitRequestRef.current;
    setSplitLoads((current) => ({ ...current, [submissionId]: { loading: true, error: '' } }));
    proposeQuestionSplit(submissionId)
      .then((proposal) => {
        if (request !== splitRequestRef.current) return;
        onQuestionSplitChange?.(studentId, splitStateFromProposal(proposal));
        setSplitLoads((current) => ({ ...current, [submissionId]: { loading: false, error: '' } }));
      })
      .catch((error) => {
        if (request !== splitRequestRef.current) return;
        setSplitLoads((current) => ({
          ...current,
          [submissionId]: {
            loading: false,
            error: error?.message || 'We could not work out where each question starts. Try again.',
          },
        }));
      });
  }, [onQuestionSplitChange]);
  useEffect(() => {
    if (!multiQuestion || !activeSplitSubmissionId || hasActiveSplit || activeIsConverting || activeIsProtected) return;
    const load = splitLoads[activeSplitSubmissionId];
    if (load?.loading || load?.error) return;
    loadSplit(activeSplitSubmissionId, activeSplitStudentId);
  }, [multiQuestion, activeSplitSubmissionId, activeSplitStudentId, hasActiveSplit, activeIsConverting, activeIsProtected, splitLoads, loadSplit]);

  useEffect(() => {
    setResponseTexts((current) => {
      const next = { ...current };
      let hasChanges = false;

      students.forEach((student) => {
        const propsText = student.responseText || '';
        const lastKnownText = lastPropsResponseTextRef.current[student.id];

        // Update if:
        // 1. No current value exists (first time), OR
        // 2. Props text changed from what we last knew (incoming update from parent)
        if (next[student.id] === undefined || propsText !== lastKnownText) {
          next[student.id] = propsText;
          lastPropsResponseTextRef.current[student.id] = propsText;
          hasChanges = true;
        }
      });

      return hasChanges ? next : current;
    });
  }, [students]);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(studentsWithPages.length - 1, 0)));
  }, [studentsWithPages.length]);

  useEffect(() => {
    setGalleryState(null);
  }, [activeIndex]);

  const activeStudent = studentsWithPages[activeIndex];
  const activeSubmission = submissions.find(row => String(row.studentId) === String(activeStudent?.id));
  // `submissions` is already this batch's rows, so their ids are the batch's.
  const batchIds = useMemo(() => submissions.map(row => row.id), [submissions]);
  const isReadOnly = isSubmitting || protectedStudentIds.has(String(activeStudent?.id)) ||
    (isManualReview
      ? activeSubmission?.status !== 'DRAFT' && !isBatchResubmission(activeSubmission, batchIds)
      : Boolean(assignmentId) && (!activeSubmission || !isBulkResponseEditable(activeSubmission, batchIds)));
  const originalPages = activeStudent?.originalPages || [];
  const hasPages = (activeStudent?.pages?.length || 0) > 0 || originalPages.length > 0;
  const activeOriginal = originalPages[0];

  // Check if all pages are manual text
  const allPagesAreManual = originalPages.length > 0 &&
    originalPages.every(page => page?.source === 'manual');

  const isManualText = allPagesAreManual;

  /**
   * Auto-save after 4 seconds of inactivity
   * Saves to backend so Continue button fallback still works
   */
  const autoSaveEdit = useCallback(
    debounce(async (studentId, text) => {
      // Manual completion saves the entire current batch before submitting it.
      // A delayed autosave could otherwise land after submit and alter reviewed work.
      if (isManualReview || isSubmitting) return;
      const submission = submissions.find(s => String(s.studentId) === String(studentId));
      if (!submission?.id || !isBulkResponseEditable(submission, submissions.map(row => row.id)) ||
          protectedStudentIdsRef.current.has(String(studentId))) {
        return;
      }

      const attachmentGeneration = Number(submission.answers?.[0]?.answer?.attachmentGeneration) || 0;

      try {
        await saveReviewedAnswer(
          submission.id,
          1,
          { answer: text, highlights: [] },
          { attachmentGeneration, suppressToast: true }
        );
      } catch (error) {
        // Auto-save failed silently
      }
    }, 4000),
    [submissions, isManualReview, isSubmitting]
  );

  // Cleanup debounced function
  useEffect(() => {
    return () => {
      autoSaveEdit.cancel();
    };
  }, [autoSaveEdit]);

  const goToStudent = (index) => {
    if (index < 0 || index >= studentsWithPages.length) return;
    setActiveIndex(index);
  };

  const openGallery = (studentId, pageId) => {
    setGalleryState({ studentId, pageId });
  };

  const galleryStudent = galleryState
    ? students.find((student) => student.id === galleryState.studentId)
    : null;
  const galleryPages = reviewPagesOf(galleryStudent, pageImagesForStudent);

  /**
   * The student's whole response, not one page's copy of it.
   *
   * A submission holds ONE answer per student, and extraction stamps that answer
   * onto every page routed to them — so reading page 0 here showed a two-page
   * student the same text the row already carried, and now that the editor is
   * persisted, saving it back would be saving a fragment over the whole. What is
   * shown has to be the unit that gets stored.
   */
  const activeResponseText =
    responseTexts[activeStudent?.id] ?? activeStudent?.responseText ?? '';

  /**
   * Quill only emits on `source === 'user'`, so every call here is a real edit —
   * which is what lets the flow treat this map as "what the teacher changed"
   * rather than "what was on screen".
   */
  const handleResponseChange = (nextText) => {
    if (!activeStudent?.id || isReadOnly) return;

    // Update local state immediately (for UI)
    setResponseTexts((current) => ({
      ...current,
      [activeStudent.id]: nextText,
    }));

    // Update parent state (for Continue fallback)
    onEditResponse?.(activeStudent.id, nextText);

    // Auto-save after 4 seconds
    if (!isManualReview) autoSaveEdit(activeStudent.id, nextText);
  };

  return (
    <>
      <BulkUploadReviewLayout>
        <BulkUploadReviewMain>
          <BulkUploadSectionTitle>Review</BulkUploadSectionTitle>

        <BulkUploadReviewNav>
          <BulkUploadReviewNavButton
            type="button"
            disabled={activeIndex === 0}
            onClick={() => goToStudent(activeIndex - 1)}
          >
            <img src={arrowLeft} alt="" width={16} height={16} />
            Previous
          </BulkUploadReviewNavButton>

          <BulkUploadReviewNavCenter>
            <BulkUploadReviewStudentName>
              {activeStudent?.name || 'Unknown Student'}
            </BulkUploadReviewStudentName>
            <BulkUploadReviewStudentMeta>
              Student {activeIndex + 1} of {studentsWithPages.length}
            </BulkUploadReviewStudentMeta>
          </BulkUploadReviewNavCenter>

          <BulkUploadReviewNavButton
            type="button"
            disabled={activeIndex >= studentsWithPages.length - 1}
            onClick={() => goToStudent(activeIndex + 1)}
          >
            Next
            <img src={arrowRight} alt="" width={16} height={16} />
          </BulkUploadReviewNavButton>
        </BulkUploadReviewNav>

        <BulkUploadReviewViewer>
          <BulkUploadReviewViewerHeader>
            {!isManualText && (
              <BulkUploadReviewViewOriginal
                type="button"
                onClick={() => openGallery(activeStudent?.id, activeOriginal?.id)}
                disabled={!hasPages}
              >
                <img src={eyeIcon} alt="" width={16} height={16} />
                View original
              </BulkUploadReviewViewOriginal>
            )}
          </BulkUploadReviewViewerHeader>

          {activeIsProtected && (
            <BulkUploadReviewEmptyState>
              This response has already been submitted. Continue with the remaining responses.
            </BulkUploadReviewEmptyState>
          )}
          {hasPages && multiQuestion && !activeIsProtected ? (
            isStudentConverting(activeStudent) ? (
              <BulkUploadReviewEmptyState>
                {editorPlaceholder({ isConverting: true, isManualText })}
              </BulkUploadReviewEmptyState>
            ) : (
              <BulkUploadQuestionSplitReview
                state={questionSplits?.[String(activeStudent?.id)] || null}
                // No row yet means the roster read has not landed; the proposal follows it.
                isLoading={!activeSubmission?.id || Boolean(splitLoads[activeSubmission?.id]?.loading)}
                loadError={splitLoads[activeSubmission?.id]?.error || ''}
                onRetry={() => activeSubmission?.id && loadSplit(activeSubmission.id, activeStudent?.id)}
                isReadOnly={isReadOnly}
                onChange={(next) => onQuestionSplitChange?.(activeStudent?.id, next)}
              />
            )
          ) : hasPages ? (
            <RichTextEditorWrapper
              text={activeResponseText}
              onChange={handleResponseChange}
              studentId={activeStudent?.id}
              isManualText={isManualText}
              isConverting={isStudentConverting(activeStudent)}
              isReadOnly={isReadOnly}
            />
          ) : (
            <BulkUploadReviewEmptyState>
              No pages assigned to this student
            </BulkUploadReviewEmptyState>
          )}

          <BulkUploadReviewPagination>
            {studentsWithPages.map((student, index) => (
              <BulkUploadReviewPageDot
                key={student.id}
                type="button"
                $active={index === activeIndex}
                aria-label={`Student ${index + 1}: ${student.name}`}
                onClick={() => goToStudent(index)}
              />
            ))}
          </BulkUploadReviewPagination>
        </BulkUploadReviewViewer>
      </BulkUploadReviewMain>

      <BulkUploadStudentListPanel>
        <BulkUploadStudentListHeader>
          <BulkUploadSectionTitle $plain>Student list</BulkUploadSectionTitle>
          <QuestionTooltip text={STUDENT_LIST_HELP_TEXT}>
            <span style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src={helpIcon} alt="" width={20} height={20} aria-hidden="true" />
            </span>
          </QuestionTooltip>
        </BulkUploadStudentListHeader>

        <BulkUploadStudentList>
          {students.map((student, index) => {
            const isActive = student.id === activeStudent?.id;
            const isExpanded = student.id === expandedStudentId;
            const hasPages = (student.pages?.length || 0) > 0;
            const indexInFiltered = studentsWithPages.findIndex(s => s.id === student.id);

            const handleClick = () => {
              if (!hasPages) return;
              if (indexInFiltered >= 0) {
                goToStudent(indexInFiltered);
              }
              setExpandedStudentId((current) =>
                current === student.id ? null : student.id,
              );
            };

            return (
              <BulkUploadStudentCard key={student.id} $active={isActive}>
                <BulkUploadStudentCardRow
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  style={{ cursor: hasPages ? 'pointer' : 'default', opacity: hasPages ? 1 : 0.5 }}
                  onClick={handleClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClick();
                    }
                  }}
                >
                  <div style={{ width: '24px', height: '24px', flexShrink: 0, borderRadius: '50%', overflow: 'hidden' }}>
                    <Avatar
                      title={false}
                      size={24}
                      variant="pixel"
                      name={student.name || 'Unknown Student'}
                      square={false}
                    />
                  </div>
                  <BulkUploadStudentInfo>
                    <BulkUploadStudentName>
                      {student.name || 'Unknown Student'}
                    </BulkUploadStudentName>
                    <BulkUploadStudentPages>
                      {formatPagesLabel(student.pages)}
                    </BulkUploadStudentPages>
                    <ExtractionChip student={student} />
                  </BulkUploadStudentInfo>
                  {isActive && (
                    <BulkUploadStudentCardActions>
                      <BulkUploadStudentCardIcon aria-hidden="true">
                        <img
                          src={isExpanded ? arrowUp : arrowDown}
                          alt=""
                          width={16}
                          height={16}
                        />
                      </BulkUploadStudentCardIcon>
                    </BulkUploadStudentCardActions>
                  )}
                </BulkUploadStudentCardRow>
                {isExpanded && (
                  <BulkUploadStudentThumbs>
                    {reviewPagesOf(student, pageImagesForStudent).length > 0 ? (
                      reviewPagesOf(student, pageImagesForStudent).map((page) => {
                        if (!page?.id) {
                          return null;
                        }
                        return (
                          <ReviewPageThumb
                            key={page.id}
                            page={page.thumbUrl ? { ...page, thumbnail: page.thumbUrl } : page}
                            currentResponseText={responseTexts[student.id]}
                            onOpenGallery={(pageId) => openGallery(student.id, pageId)}
                          />
                        );
                      })
                    ) : (
                      <div style={{
                        padding: '20px',
                        color: '#999',
                        fontSize: '12px',
                        textAlign: 'center',
                        minHeight: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        No pages assigned
                      </div>
                    )}
                  </BulkUploadStudentThumbs>
                )}
              </BulkUploadStudentCard>
            );
          })}
        </BulkUploadStudentList>
      </BulkUploadStudentListPanel>
    </BulkUploadReviewLayout>

    {galleryState && galleryPages.length > 0 && (
      <BulkUploadPageGallery
        pages={galleryPages}
        initialPageId={galleryState.pageId}
        onClose={() => setGalleryState(null)}
      />
    )}
    </>
  );
};

// Whitespace-only markup is what an emptied Quill editor leaves behind.
const hasResponseText = (student) => Boolean(htmlToPlainText(student.responseText));

const isRouted = (student) => (student?.pages?.length || 0) > 0;

/**
 * Empty and still-converting are different problems with different fixes, and
 * this dialog used to conflate them.
 *
 * A converting response has pages and no text, which is the exact shape of an
 * empty one — so once Review became reachable before extraction finished, every
 * converting student was reported as empty and the teacher was told to "go back
 * and type them in", which is the one thing they cannot do: that editor is locked
 * precisely because its text is about to arrive.
 *
 * Nor is a converting response one the teacher needs to act on. The server waits
 * for its text and retries before it gives up, so it is reported here rather than
 * warned about.
 */
/**
 * "Might text still arrive?" and "is there text now?" are different questions and
 * they take different answers on a row we know nothing about.
 *
 * The LOCK asks the first, and treats unknown as yes — the safe direction, because
 * guessing wrong loses what the teacher typed.
 *
 * This warning asks the second, and treats unknown as no. A row with no status is
 * a row nothing has told us about; calling it "still converting" would be a
 * reassurance we have not earned, and BU-16's rule — no text is no text — is the
 * honest reading. Only a row the server has actually said is in flight is excluded.
 */
const isExplicitlyConverting = (student) =>
  extractedPagesOf(student).some((page) =>
    CONVERTING_STATUSES.has(page?.extractionStatus),
  );

const emptyResponsesIn = (students) =>
  students.filter(
    (s) => isRouted(s) && !isExplicitlyConverting(s) && !hasResponseText(s),
  );

const convertingResponsesIn = (students) =>
  students.filter((s) => isRouted(s) && isExplicitlyConverting(s));

/**
 * One line, not two.
 *
 * This dialog used to spell out each situation separately — what an empty
 * response costs, what a converting one is doing — and a teacher standing at
 * Continue had to read both and work out which applied to whom. Neither sentence
 * was wrong; together they were a paragraph answering a question nobody asked.
 *
 * What they need is the count, the reason, and the choice. Which student is which
 * is already on the cards behind the dialog: a `Converting…` chip, or nothing.
 *
 * Two earlier drafts also claimed more than this dialog can know — "go back and
 * type them in" is impossible once the row is locked, and "marking them spends a
 * run on nothing" is untrue in the wizard flow (Continue only opens the marking
 * step) and for a class with JeddAI off (a person marks it, no run). It now says
 * only what is true of the responses.
 */
const notReadyClause = ({ empty, converting }) => {
  const total = empty + converting;
  const responses = `${total} ${total === 1 ? 'response is' : 'responses are'}`;
  if (empty && converting) return `${responses} empty or still extracting.`;
  return empty ? `${responses} empty.` : `${responses} still extracting.`;
};

// The way out, offered every time — Check again sits on the banner behind this.
const WHAT_TO_DO = 'If you reassigned pages in the previous step, the system needs time to process the changes. Continuing before this completes will cause errors. Please wait a moment and try again.';

const beforeContinueMessage = (counts) =>
  `${notReadyClause(counts)} ${WHAT_TO_DO}`;

const BEFORE_CONTINUE_TITLE = "Some responses aren't ready";

const unresolvedSplitsMessage = (names = []) =>
  `${names.join(', ')}: every question needs text, a typed answer, or a not-attempted decision before marking can start.`;

/** 06 §1.3: a student whose pages could not be read gets one targeted retry, not a dead end. */
export const extractionRetryMessage = (count) =>
  `We couldn't read ${count} ${count === 1 ? "student's" : "students'"} pages.`;

const EXTRACTION_RETRY_START_FAILED = "We couldn't start reading those pages again. Please try again.";

const ExtractionRetryBanner = ({ extraction }) => {
  const failedIds = extraction?.failedSubmissionIds || [];
  const [startFailed, setStartFailed] = useState(false);
  if (!failedIds.length) return null;
  const tryAgain = () => {
    if (extraction.isStarting) return;
    setStartFailed(false);
    Promise.resolve(extraction.start({ submissionIds: failedIds })).catch(() => setStartFailed(true));
  };
  return (
    <div data-testid="review-extraction-retry" data-failed-count={failedIds.length}>
      <TopBannerBox
        bannerMessage={startFailed ? EXTRACTION_RETRY_START_FAILED : extractionRetryMessage(failedIds.length)}
        confirmButtonLabel="Try reading again"
        onConfirm={tryAgain}
      />
    </div>
  );
};

export const BulkUploadReviewStep = ({
  currentStep = 4,
  steps,
  mode = 'schoolTeacher',
  rows = [],
  assignmentId,
  submissionIds,
  isManualReview = false,
  isSubmitting = false,
  feedbackStatus,
  submitError,
  // Status refreshes can reveal extraction still pending in a resumed batch.
  extractionMessage = '',
  onCheckExtraction,
  // The assignment's extraction run: its failed ids drive the "Try reading again" banner.
  extraction,
  onBack,
  onClose,
  onContinue,
  // Raised per keystroke rather than at Continue: the teacher can also leave this
  // step by the stepper or the close button, and an edit that only survives one
  // of the three exits is the bug this prop exists to close.
  onEditResponse,
  continueLabel = 'Continue',
  isContinueDisabled = false,
  fullPage = false,
  multiQuestion = false,
  questionSplits,
  onQuestionSplitChange,
  // Bulk Upload v2: studentId → [{pageId, previewUrl, thumbUrl}] (06 §7.3).
  pageImagesForStudent,
}) => {
  const [students, setStudents] = useState(() => buildStudents(rows));
  const rowsRef = useRef(rows);
  const protectedStudentIds = useMemo(() => new Set(isManualReview ? [] :
    (feedbackStatus?.submissions || []).filter(row =>
      ['GENERATING', 'READY', 'FAILED'].includes(feedbackStatusOf(row)) ||
      // A batch resubmission awaiting its redo (NOT_REQUESTED) is the teacher's
      // to correct, like a draft (06 §5.8).
      (row.status && row.status !== 'DRAFT' &&
        !isBatchResubmission(row, submissionIds, feedbackStatusOf(row))),
    ).map(row => String(row.studentId))), [feedbackStatus, isManualReview, submissionIds]);

  useEffect(() => {
    // Only rebuild if rows actually changed (not just reference changed)
    if (rowsRef.current === rows) return;
    rowsRef.current = rows;
    setStudents(buildStudents(rows));
  }, [rows]);

  /**
   * FE-BU16: an empty response marks nothing, so continuing past one is a decision
   * rather than the default. Two counts, not one: a converting response is not an
   * empty one, and telling a teacher to type in text that is already on its way is
   * how they end up doing the server's work for it.
   */
  const [beforeMarking, setBeforeMarking] = useState(null);

  /**
   * Multi-question tasks: a question nobody decided on cannot be marked, so unlike
   * an empty single-question response it is not something to continue past. The
   * teacher is sent back to the response, by name.
   */
  const unresolvedSplitNames = () => {
    if (!multiQuestion) return [];
    // A student still converting has no split yet; the converting popup owns them.
    return students
      .filter((student) => (student.pages?.length || 0) > 0)
      .filter((student) => !protectedStudentIds.has(String(student.id)))
      .filter((student) => !isStudentConverting(student))
      .filter((student) => !isSplitResolved(questionSplits?.[String(student.id)]))
      .map((student) => student.name || 'Unknown Student');
  };

  const askOrContinue = () => {
    if (isContinueDisabled || isSubmitting) return;
    const counts = {
      empty: emptyResponsesIn(students.filter(student => !protectedStudentIds.has(String(student.id)))).filter((student) =>
        !multiQuestion || !isSplitResolved(questionSplits?.[String(student.id)])
      ).length,
      converting: convertingResponsesIn(students.filter(student => !protectedStudentIds.has(String(student.id)))).length,
      unresolvedSplits: unresolvedSplitNames(),
    };
    if (counts.unresolvedSplits.length) return setBeforeMarking(counts);
    if (!counts.empty && !counts.converting) return onContinue?.();
    return setBeforeMarking(counts);
  };

  const continueAnyway = () => {
    if (isContinueDisabled || isSubmitting) return;
    setBeforeMarking(null);
    // An undecided question is never continued past; the popup only sends them back.
    if (beforeMarking?.unresolvedSplits?.length) return;
    onContinue?.();
  };

  // Test data attributes
  const studentsWithPages = students.filter(s => (s.pages?.length || 0) > 0);
  const studentsWithText = students.filter(s => s.responseText && s.responseText.length > 0);

  return (
    <BulkUploadWizardShell
      currentStep={currentStep}
      steps={steps}
      onBack={isSubmitting ? undefined : onBack}
      onClose={onClose}
      onContinue={askOrContinue}
      continueLabel={continueLabel}
      isContinueDisabled={isContinueDisabled}
      fullPage={fullPage}
      data-component="bulk-upload-review"
      data-unit="bulk-upload-step-4"
      data-total-students={students.length}
      data-students-with-pages={studentsWithPages.length}
      data-students-with-text={studentsWithText.length}
      data-continue-disabled={isContinueDisabled}
      data-multi-question={multiQuestion}
    >
      {submitError && (
        <TopBannerBox
          bannerMessage={submitError}
          confirmButtonLabel="Try again"
          onConfirm={askOrContinue}
        />
      )}
      {extractionMessage && (
        <TopBannerBox
          bannerMessage={extractionMessage}
          confirmButtonLabel="Check again"
          onConfirm={onCheckExtraction}
        />
      )}
      <ExtractionRetryBanner extraction={extraction} />
      <BulkUploadTeacherReview
        students={students}
        assignmentId={assignmentId}
        submissionIds={submissionIds}
        isManualReview={isManualReview}
        protectedStudentIds={protectedStudentIds}
        isSubmitting={isSubmitting}
        onEditResponse={onEditResponse}
        multiQuestion={multiQuestion}
        questionSplits={questionSplits}
        onQuestionSplitChange={onQuestionSplitChange}
        pageImagesForStudent={pageImagesForStudent}
      />
      {beforeMarking && beforeMarking.unresolvedSplits?.length > 0 && (
        <GeneralPopup
          icon={warningIcon}
          title="Some questions still need a decision"
          textContent={unresolvedSplitsMessage(beforeMarking.unresolvedSplits)}
          buttonText="Back to Review"
          closeBtnText="Close"
          confirmButtonAction={() => setBeforeMarking(null)}
          hidePopup={() => setBeforeMarking(null)}
        />
      )}
      {beforeMarking && !beforeMarking.unresolvedSplits?.length && (
        <GeneralPopup
          icon={warningIcon}
          title={BEFORE_CONTINUE_TITLE}
          textContent={beforeContinueMessage(beforeMarking)}
          buttonText="Continue anyway"
          closeBtnText="Go back"
          confirmButtonAction={continueAnyway}
          hidePopup={() => setBeforeMarking(null)}
        />
      )}
    </BulkUploadWizardShell>
  );
};
